import { z } from 'zod';
import { UUIDV7_PATTERN } from '../core/id';
import { LOCAL_DATE_RE, LOCAL_TIME_RE } from '../core/time';

/** A lowercase uuidv7. Every row id and event id. */
export const IdZ = z.string().regex(UUIDV7_PATTERN, 'Expected a uuidv7');
/** Epoch ms. */
export const TsZ = z.number().int().nonnegative();
export const LocalDateZ = z.string().regex(LOCAL_DATE_RE, 'Expected YYYY-MM-DD');
export const LocalTimeZ = z.string().regex(LOCAL_TIME_RE, 'Expected HH:MM');
export const HexColorZ = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #rrggbb');
/** sha256 hex of a media file. */
export const MediaHashZ = z.string().regex(/^[0-9a-f]{64}$/);
/** Team side in a game. */
export const TeamZ = z.enum(['A', 'B']);
export type Team = z.infer<typeof TeamZ>;

/** One-line human text: trimmed, no control characters. */
export const textZ = (min: number, max: number) =>
  z
    .string()
    .min(min)
    .max(max)
    // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control chars is the point
    .regex(/^[^\u0000-\u0008\u000b-\u001f\u007f]*$/, 'No control characters');

/** A display name, Greek or Latin letters welcome. */
export const NameZ = textZ(1, 40);

export const OkZ = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof OkZ>;

/**
 * Query helpers. Query strings arrive as strings; the client sends numbers and booleans
 * through encodeQuery (booleans as 1/0). These accept both forms.
 */
export const qInt = (min: number, max: number) =>
  z
    .union([z.number(), z.string().regex(/^-?\d+$/)])
    .transform((v) => Number(v))
    .pipe(z.number().int().min(min).max(max));

export const qBool = z
  .union([z.boolean(), z.enum(['0', '1', 'true', 'false'])])
  .transform((v) => v === true || v === '1' || v === 'true');

/** Repeated keys (`?id=a&id=b`) or a comma list (`?id=a,b`). */
export const qList = <T extends z.ZodType<string, string>>(item: T, max = 100) =>
  z
    .union([z.array(z.string()), z.string()])
    .transform((v) => (Array.isArray(v) ? v : v.split(',').filter(Boolean)))
    .pipe(z.array(item).max(max));

/** Keyset paging by a timestamp or id cursor. */
export const PageQueryZ = z.object({
  before: z.string().max(80).optional(),
  limit: qInt(1, 100).optional(),
});
